import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

export type TabType = {
  label: string;
  code: string;
};

export type SelectedTabType = {
  index: number;
} & TabType;

export type UseTabNavigationProps = {
  tabs: TabType[];
  initialTabCode?: string;
  loadingData?: boolean;
  pathPrefix?: string;
  getSearch?: (searchParams: URLSearchParams) => string;
  skip?: boolean;
};

const getPathPrefix = (
  pathPrefix: string,
  id: string | undefined,
  caseTypeCode: string | undefined
) => {
  let processedPathPrefix = pathPrefix;
  if (id) {
    processedPathPrefix = processedPathPrefix.replace(":id", id);
  }
  if (caseTypeCode) {
    processedPathPrefix = processedPathPrefix.replace(":caseTypeCode", caseTypeCode);
  }
  return processedPathPrefix;
};

export const useTabNavigation = ({
  initialTabCode,
  tabs,
  loadingData,
  pathPrefix = "",
  getSearch,
  skip = false,
}: UseTabNavigationProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState<SelectedTabType | undefined>();
  const { id, code, caseTypeCode } = useParams<{
    id: string;
    code: string;
    caseTypeCode?: string;
  }>();

  const processedPathPrefix = getPathPrefix(pathPrefix, id, caseTypeCode);

  const setTab = useCallback(
    (code: string) => {
      const foundTabIndex = tabs.findIndex((tab) => tab.code === code);
      const tabIndex = foundTabIndex === -1 ? 0 : foundTabIndex;
      setSelectedTab({ ...tabs[tabIndex], index: tabIndex });
    },
    [tabs]
  );

  useEffect(() => {
    if (loadingData || skip) return;

    if (
      (code === undefined || tabs.findIndex((tab) => tab.code === code) === -1) &&
      initialTabCode !== undefined
    ) {
      navigate(
        {
          pathname: `${processedPathPrefix}/${initialTabCode}`,
        },
        { replace: true }
      );
    }

    if (code === undefined) {
      navigate(
        {
          pathname: `${processedPathPrefix}/${tabs[0].code}`,
        },
        { replace: true }
      );
    }

    if (code && code !== selectedTab?.code) {
      setTab(code);
      return;
    }
  }, [
    processedPathPrefix,
    loadingData,
    navigate,
    selectedTab,
    initialTabCode,
    code,
    setTab,
    tabs,
    skip,
  ]);

  const navigateTab = useCallback(
    (index: number) => {
      const tab = tabs[index] ?? tabs[0];
      setTab(tab.code);
      navigate({
        pathname: `${processedPathPrefix}/${tab.code}`,
        search: getSearch?.(searchParams) ?? "",
      });
    },
    [tabs, setTab, navigate, processedPathPrefix, getSearch, searchParams]
  );

  return { selectedTab, navigateTab };
};
